import * as rax from 'retry-axios';
import { AxiosError } from 'axios';
import { max, min } from 'lodash';
import {
    IChildLayoutResponse,
    IContainerBlockResponse,
    IFlowApplyActionResponse,
    IFlowResponse,
    ITransformedFlowResponse,
    TBlockResponse
} from '~/types/server.types';
import { isNetworkError } from '~/utils/network.utils';
import { Action } from '~/services/actions-manager/action';
import { LogEvents } from '~/services/fullstory/log-events';
import {
    assignBlockParentId,
    transformFlowResponse
} from '~/utils/server-response.utils';
import { FlowAdapter } from '~/services/adapters/flow.adapter';
import { CompanyStore } from '~/stores/company.store';
import { IApiService } from '~/services/interfaces/IApiService';
import { ConfigStore } from '~/stores/config.store';
import { UserStore } from '~/stores/user.store';
import { FullstoryService } from '~/services/fullstory/fullstory.service';
import { WebSocketsService } from '~/controllers/web-sockets.service';
import { ITemplateBase } from '~/types/model.types';

export interface IFetchFlowOptions {
    preview: boolean;
    active: boolean;
    regainControlFingerprint: string;
}

export const FetchFlowOptionsDefaults = {
    preview: false,
    active: false,
    regainControlFingerprint: null
};

export class FlowService extends FlowAdapter {
    companyStore: CompanyStore;
    configStore: ConfigStore;
    userStore: UserStore;
    fullstoryService: FullstoryService;
    webSocketsService: WebSocketsService;

    constructor({
        companyStore,
        userStore,
        configStore,
        apiService,
        webSocketsService,
        fullstoryService
    }: {
        companyStore: CompanyStore;
        apiService: IApiService;
        configStore: ConfigStore;
        userStore: UserStore;
        fullstoryService: FullstoryService;
        webSocketsService: WebSocketsService;
    }) {
        super(apiService);

        this.companyStore = companyStore;
        this.configStore = configStore;
        this.userStore = userStore;
        this.webSocketsService = webSocketsService;
        this.fullstoryService = fullstoryService;
    }

    async fetchFlow(
        flowId: string,
        {
            active = FetchFlowOptionsDefaults.active,
            ...fetchFlowOptions
        }: IFetchFlowOptions = FetchFlowOptionsDefaults
    ): Promise<ITransformedFlowResponse> {
        const response = await super._fetchFlow(flowId, {
            active,
            ...fetchFlowOptions
        });

        if (active) {
            // for active flow, we remove the last_action_number field from the response
            // we only need it for the draft revision, and it causes issues
            // when it's overwritten by the active revision
            delete response.last_action_number;
        }

        return transformFlowResponse(
            response,
            this.companyStore.currCompany?.default_timezone
        );
    }

    async getFlowRevisions(id: string) {
        const response = await super._getFlowRevisions(id);

        // Return an array sorted by published_at date. Last published version is the first.
        return response.revisions.sort((a, b) =>
            a.published_at > b.published_at ? -1 : 1
        );
    }

    async getFlowByRevision(flowId: string, revisionId: string) {
        const response = await super._getFlowByRevision(flowId, revisionId);

        return transformFlowResponse(
            response,
            this.companyStore.currCompany?.default_timezone
        );
    }

    async mergeFlowRevisions(id: string) {
        const response = await super._mergeFlowRevisions(id);

        return transformFlowResponse(
            response,
            this.companyStore.currCompany?.default_timezone
        );
    }

    async discardDraft(id: string) {
        const response = await super._discardDraft(id);

        return transformFlowResponse(
            response,
            this.companyStore.currCompany?.default_timezone
        );
    }

    async applyActions(
        flowId: string,
        actions
    ): Promise<IFlowApplyActionResponse> {
        const { sessionId } = this.configStore;
        const { fingerprint } = this.userStore;

        return super._applyActions({
            flowId,
            actions,
            fingerprint,
            sessionId,
            wasWebsocketActive: this.webSocketsService.isAuthorized,
            onRetryAttempt: () =>
                this.ensureActionsAreAheadOfServer(flowId, actions),
            shouldRetry: err => this.shouldRetryOnNetworkError(err, actions)
        });
    }

    async saveAsFlowTemplate(
        _: string, // required because TemplatesViewService expects this function signature
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return super.createFlowTemplate(template._id, title);
    }

    private shouldRetryOnNetworkError(err: AxiosError, actions: Array<Action>) {
        if (!isNetworkError(err)) {
            return false;
        }

        const config = rax.getConfig(err);
        const isRetrying =
            config.currentRetryAttempt <= config.noResponseRetries;

        if (isRetrying) {
            this.fullstoryService.warn(
                LogEvents.ActionsNumbers(
                    LogEvents.ApplyActionsNetworkError(
                        config.currentRetryAttempt + 2
                    ),
                    actions
                )
            );
        }

        return isRetrying;
    }

    private async ensureActionsAreAheadOfServer(flowId: string, actions) {
        const recoveredResponse = await this.recoverFlowResponse(
            flowId,
            actions
        );
        const lastActionNumberAtServer = recoveredResponse.last_action_number;

        this.fullstoryService.info(
            LogEvents.LastActionNumberFromRecover(lastActionNumberAtServer)
        );

        const actionNumbers = actions.map(x => x.action_number);

        // Is the server is at the same state as the sent actions are
        if (
            min(actionNumbers) <= lastActionNumberAtServer &&
            max(actionNumbers) === lastActionNumberAtServer
        ) {
            this.fullstoryService.info(
                LogEvents.ActionStatusResultLog(
                    LogEvents.ActionsGotRecovered,
                    recoveredResponse.actions
                )
            );

            // We want to fail the retry attempt (that's why the Promise.reject) and return as a Promise resolve the recovered server response.
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject({
                response: {
                    data: {
                        error_type: 'HBActionSuccessInServerWithoutResponse',
                        recoveredResponse
                    }
                }
            });
        }

        this.fullstoryService.warn(
            LogEvents.ActionsNumbers(LogEvents.RetryingApplyActionsFor, actions)
        );

        return Promise.resolve(); // The actions haven't got to the server retry to apply them.
    }

    // #endregion
    // #region transformations

    // TODO: remove when response is fixed
    transformCreateOrUpdateBlockResponse(
        response: IFlowResponse,
        blockAttrs: { block_id: string }
    ): TBlockResponse[] {
        const { block_id } = blockAttrs;

        // TODO: Should this be on the server?
        const blocksWithParentId = assignBlockParentId(response.blocks);

        const allBlocks = [];

        function recursivelyFindChildren(block: TBlockResponse) {
            allBlocks.push(block);

            if (!(<IContainerBlockResponse>block).children) {
                return;
            }

            (<IContainerBlockResponse>block).children.forEach(
                (child: IChildLayoutResponse) => {
                    const childBlock = blocksWithParentId[child.block_id];

                    if ((<IContainerBlockResponse>childBlock).children) {
                        return recursivelyFindChildren(childBlock);
                    }

                    return allBlocks.push(childBlock);
                }
            );
        }

        const parentBlock = blocksWithParentId[block_id];

        recursivelyFindChildren(parentBlock);

        return allBlocks;
    }
}
